/**
 * Copyright (C) 2013-2018 MNEMOTIX <http://www.mnemotix.com/> and/or its affiliates
 * and other contributors as indicated by the @author tags.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {useEffect, useMemo, useState} from "react";
import {useApolloClient} from "@apollo/client";
import {GeolocService} from "../../../services/GeolocService";
import {finalize} from "rxjs/operators";

/**
 * @param {string} qs
 * @param {function} [isValidPlace]
 * @param {object} [userAuthenticationService]
 * @param {object} [defaultPlace]
 * @param {boolean} [citiesOnly]
 * @param {string} [country]
 * @param {array} [bbox}
 * @param {array} [featureCodes]
 * @param {GeolocService} [defaultGeolocService] - A default geoloc service. Use this parameter to share same service through an application.
 *
 * @return {{selectedPlace: Object, searchedPlaces: Array<Object>, proceedAutoPlace: Function, setPlace: Function, loading: Boolean, error: String, setPlaceFromId: Function}}
 */
export function useGeolocService({qs, isValidPlace, userAuthenticationService, defaultPlace, citiesOnly, country, bbox, featureCodes, geolocService: defaultGeolocService} = {}){
  const apolloClient = useApolloClient();
  const [searchedPlaces, setSearchedPlaces] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();

  const geolocService = defaultGeolocService || useMemo(() => {
    return new GeolocService({
      isValidPlace,
      defaultPlace,
      apolloClient,
      userAuthenticationService,
      bbox
    });
  }, [bbox]);

  const placeSearcher = useMemo(() => geolocService.getPlaceSearcher({defaultFirst: 10}), [geolocService]);

  useEffect(() => {
    const placeSearcherSubscription = placeSearcher.results.subscribe((places) => {
      setSearchedPlaces(places);
      setLoading(false);
    });

    const currentPlaceSubscription = geolocService.currentPlace.subscribe((place) => {
      setSelectedPlace(place);
    });

    return () => {
      placeSearcherSubscription.unsubscribe();
      currentPlaceSubscription.unsubscribe();
    }
  }, [geolocService]);

  useEffect(() => {
    if (placeSearcher && !!qs){
      setLoading(true);
      placeSearcher.search(qs, {citiesOnly, country, bbox, featureCodes});
    }
  }, [qs]);

  const proceedAutoPlace = useMemo(() => {
    return () => {
      if (loading) return;
      setLoading(true);
      setError(null);

      geolocService.setAutoPlace().pipe(
        finalize(() => setLoading(false))
      )
        .subscribe({
          error: (error) => {
            console.warn('geolocService.setAutoPlace() emitted the following error', error);
            setError(error);
          }
        });
    }
  }, [geolocService]);

  const setPlace =  useMemo(() => {
    return (place) => geolocService.setManualPlace(place)
  }, [geolocService]);

  const setPlaceFromId =  useMemo(() => {
    return (id) => {
      geolocService.setPlaceFromId({id});
    }
  }, [geolocService]);

  return {selectedPlace, searchedPlaces, proceedAutoPlace, setPlace, setPlaceFromId, loading, error};
}